import { memo, useLayoutEffect, useMemo, useRef, type CSSProperties } from "react";
import type { RunProjection } from "@roboscientist/contracts";
import { GANTT_AXIS_HEIGHT, GANTT_ROW_HEIGHT, ganttContentHeight } from "./chartMath.js";
import { timelineBarGeometry } from "./runPresentation.js";
import { isRunningRun, runEnd, runStart } from "./runTiming.js";
import type { ReplayClock } from "./replayClock.js";
import type { BrokenTimelineScale } from "./timelineBreaks.js";

/** The normal timeline geometry, painted once and revealed by one shared clip plane. */
export const RunReplayChart = memo(function RunReplayChart({ runs, scale, observedAt, clock, colors, gridColor, onHover, onLeave, onSelect }: {
  runs: RunProjection[];
  scale: BrokenTimelineScale;
  observedAt: number;
  clock: ReplayClock;
  colors: Record<RunProjection["kind"], string>;
  gridColor: string;
  onHover: (params: unknown) => void;
  onLeave: () => void;
  onSelect: (params: unknown) => void;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const barsRef = useRef<HTMLDivElement>(null);
  const sweepRef = useRef<HTMLDivElement>(null);
  const intervals = useMemo(() => runs.map((run) => {
    const start = runStart(run), end = Math.max(start + 1_000, runEnd(run, observedAt));
    return { start, end, geometry: timelineBarGeometry(scale, start, end) };
  }), [runs, scale, observedAt]);
  useLayoutEffect(() => {
    const root = rootRef.current, clip = barsRef.current, sweep = sweepRef.current;
    if (!root || !clip || !sweep) return;
    const bars = Array.from(root.querySelectorAll<HTMLElement>(".gantt-replay-bar"));
    const heads = Array.from(root.querySelectorAll<HTMLElement>(".gantt-replay-head"));
    const states = new Map<number, boolean>();
    const end = Math.max(...intervals.map((interval) => interval.end));
    const paint = (time: number) => {
      const x = scale.toX(time);
      // Both use this exact x in the same paint. Minimum-width bars and shadows
      // are clipped too, so nothing can be revealed beyond the replay boundary.
      root.style.setProperty("--run-replay-x", `${x}px`);
      clip.style.clipPath = time >= end ? "none" : `inset(0 calc(100% - ${x}px) 0 0)`;
      const inView = time >= scale.domainStart && time <= scale.domainEnd && time < end;
      sweep.hidden = !inView;
      intervals.forEach((interval, index) => {
        const active = inView && time >= interval.start && time < interval.end;
        if (states.get(index) === active) return;
        states.set(index, active);
        bars[index]?.classList.toggle("is-active", active);
        if (heads[index]) heads[index]!.hidden = !active;
      });
    };
    paint(clock.getTime());
    return clock.subscribe(paint);
  }, [clock, intervals, scale]);
  return <div ref={rootRef} className="gantt-replay-chart" style={{ height: ganttContentHeight(runs.length) }}>
    <div className="gantt-replay-grid" aria-hidden="true" style={{ bottom: GANTT_AXIS_HEIGHT }}>
      {Array.from({ length: 6 }, (_, index) => <i key={index} style={{ left: scale.width * index / 5, borderColor: gridColor }} />)}
    </div>
    <div ref={barsRef} className="gantt-replay-bars" style={{ bottom: GANTT_AXIS_HEIGHT }}>
      {runs.map((run, index) => {
        const { geometry } = intervals[index]!;
        return <div key={run.id} className={`gantt-replay-bar${isRunningRun(run) ? " is-saved-running" : ""}`} data-run-id={run.id}
          style={{ left: geometry.start, width: geometry.width, top: index * GANTT_ROW_HEIGHT + (GANTT_ROW_HEIGHT - 14) / 2, "--run-replay-color": colors[run.kind] } as CSSProperties}
          onMouseEnter={() => onHover({ dataIndex: index, event: { offsetX: geometry.start, offsetY: index * GANTT_ROW_HEIGHT } })}
          onMouseLeave={onLeave}
          onClick={() => onSelect({ dataIndex: index })} />;
      })}
    </div>
    <div ref={sweepRef} className="gantt-replay-sweep" aria-hidden="true" style={{ left: "var(--run-replay-x, 0px)" }}><span>REPLAY</span></div>
    {runs.map((run, index) => <i key={run.id} className="gantt-replay-head" aria-hidden="true" hidden
      style={{ left: "var(--run-replay-x, 0px)", top: index * GANTT_ROW_HEIGHT + GANTT_ROW_HEIGHT / 2, "--run-replay-color": colors[run.kind] } as CSSProperties} />)}
  </div>;
});
